import {connect} from 'react-redux';
import {createSelector} from 'reselect';

import {ErrorWithData} from '../../../../../libs/shared/helpers/src/types/customErrors';
import {onlyItem} from '../../../../../libs/shared/helpers/src/utilities/arrayHelpers';
import {reportToBugsnag} from '../../../../../srcCommon/services/bugsnag/bugsnag';
import {BulkActionTypesEnum} from '../../../../helpers/bulkActionHelpers';
import {makeMapStateToProps} from '../../../../helpers/reselectHelpers';
import {ConversationTeamStatusesEnum} from '../../../../models/api/conversationCommonTypes';
import {ConversationTrackerStatusesEnum} from '../../../../models/api/conversationTrackerModel';
import {SelectAllJobModel} from '../../../../models/api/jobModel';
import {AppState} from '../../../../store/appState';
import {getUser} from '../../../../store/data/teammates/teammatesSelectors';
import {SelectAllBanner} from './selectAllBanner';

/*
 * Props.
 */

interface SelectAllBannerContainerProps {
  job: SelectAllJobModel;
}

/*
 * Selectors.
 */

function getJob(_state: AppState, props: SelectAllBannerContainerProps) {
  return props.job;
}

const getPatch = createSelector(getJob, (job) => job.patch);
const getStarredTagId = createSelector(getUser, (user) => user.starredTagId);

const getJobType = createSelector([getPatch, getStarredTagId], (patch, starredTagId) => {
  const {inboxId, addTags, removeTags, isSeen, addTrackers, assigneeId, teamStatus} = patch;
  if (inboxId) {
    return BulkActionTypesEnum.MOVE;
  }

  if (starredTagId && addTags && onlyItem(addTags) === starredTagId) {
    return BulkActionTypesEnum.STAR;
  }

  if (starredTagId && removeTags && onlyItem(removeTags) === starredTagId) {
    return BulkActionTypesEnum.UNSTAR;
  }

  if (teamStatus === ConversationTeamStatusesEnum.ARCHIVED) {
    return BulkActionTypesEnum.ARCHIVE;
  }

  if (teamStatus === ConversationTeamStatusesEnum.OPEN) {
    return BulkActionTypesEnum.REOPEN;
  }

  if (teamStatus === ConversationTeamStatusesEnum.TRASHED) {
    return BulkActionTypesEnum.TRASH;
  }

  if (teamStatus === ConversationTeamStatusesEnum.SPAM) {
    return BulkActionTypesEnum.SPAM;
  }

  if (addTags && addTags.length > 0) {
    return BulkActionTypesEnum.TAG;
  }

  if (removeTags && removeTags.length > 0) {
    return BulkActionTypesEnum.UNTAG;
  }

  if (isSeen === true) {
    return BulkActionTypesEnum.MARK_READ;
  }

  if (isSeen === false) {
    return BulkActionTypesEnum.MARK_UNREAD;
  }

  if (assigneeId === null) {
    return BulkActionTypesEnum.UNASSIGN;
  }

  if (assigneeId) {
    return BulkActionTypesEnum.ASSIGN;
  }

  if (isSeen) {
    return BulkActionTypesEnum.MARK_READ;
  }

  const onlyAddTracker = addTrackers && onlyItem(addTrackers);
  if (onlyAddTracker?.status === ConversationTrackerStatusesEnum.INBOX && onlyAddTracker?.remindAt === null) {
    return BulkActionTypesEnum.UNSNOOZE;
  }

  if (onlyAddTracker?.status === ConversationTrackerStatusesEnum.INBOX) {
    return BulkActionTypesEnum.REOPEN;
  }

  if (onlyAddTracker?.status === ConversationTrackerStatusesEnum.ARCHIVED && onlyAddTracker?.remindAt) {
    return BulkActionTypesEnum.SNOOZE;
  }

  if (onlyAddTracker?.status === ConversationTrackerStatusesEnum.ARCHIVED) {
    return BulkActionTypesEnum.ARCHIVE;
  }

  if (onlyAddTracker?.status === ConversationTrackerStatusesEnum.TRASHED) {
    return BulkActionTypesEnum.TRASH;
  }

  if (onlyAddTracker?.status === ConversationTrackerStatusesEnum.SPAM) {
    return BulkActionTypesEnum.SPAM;
  }

  reportToBugsnag(new ErrorWithData('Unknown select all job type', {patch}));
  return undefined;
});

/*
 * Redux.
 */

const mapStateToProps = makeMapStateToProps<SelectAllBannerContainerProps>()(() => ({
  jobType: getJobType,
}));

export const SelectAllBannerContainer = connect(mapStateToProps)(SelectAllBanner);
